import { AgentHighlights } from "@/components/AgentHighlights";
import { BooGradientText } from "@/components/BooLogo";
import { ErrorComponent, InlineError, LoadingIndicator } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useSession } from "@/lib/auth-client";
import { api, type BooMapLink, type ContainerRow, type Posture } from "@/lib/api";
import { BooOrgChart } from "@/components/boo/BooMap";
import { BooList } from "@/components/boo/BooList";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { defaultBoosView, isBoosView, storeBoosView, storedBoosView, type BoosView } from "@/lib/boos-view";
import { RequestAccessSheet } from "@/components/grants/RequestAccessSheet";
import { POSTURE_LABELS } from "@/lib/posture";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronDown, List, Network, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useOrgRole } from "@/lib/use-org-role";
import { toast } from "sonner";

const createSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be 100 characters or fewer"),
  posture: z.enum(["teammate", "chief-of-staff"]),
  description: z.string().max(1000, "Description must be 1000 characters or fewer").optional(),
  // Empty means the creator owns it.
  ownerUserId: z.string().optional(),
});
type CreateForm = z.infer<typeof createSchema>;

// The two shapes a Boo can take, chosen up front: switching later from the
// General tab restarts a connected Boo, so the cheap moment is creation.
const POSTURE_OPTIONS: Array<{ value: Posture; blurb: string }> = [
  { value: "teammate", blurb: "Works with the whole team in channels and DMs." },
  {
    value: "chief-of-staff",
    blurb: "Works for one person: inbox, calendar, briefings. Talks only to you until you add someone.",
  },
];

function CreateContainerDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  // ownerName: set when the Boo was created for someone else.
  onCreated: (c: ContainerRow, ownerName: string | null) => void;
}) {
  const queryClient = useQueryClient();

  const form = useForm<CreateForm>({
    resolver: zodResolver(createSchema),
    defaultValues: { name: "", posture: "teammate" as const, description: "", ownerUserId: "" },
  });
  // Org owners and admins can set up a Boo for someone else in the org; that
  // person becomes its first owner instead of the creator.
  const { org } = useOrgRole();
  const { data: session } = useSession();
  type OrgMember = { user: { id: string; name?: string | null; email?: string | null } };
  const orgPeople = ((org?.members ?? []) as OrgMember[]).filter((m) => m.user.id !== session?.user.id);

  const mutation = useMutation({
    mutationFn: (data: CreateForm) =>
      api.containers.create({
        name: data.name,
        description: data.description || undefined,
        posture: data.posture,
        ownerUserId: data.ownerUserId || undefined,
      }),
    onSuccess: (created, data) => {
      queryClient.invalidateQueries({ queryKey: ["containers"] });
      queryClient.invalidateQueries({ queryKey: ["billing-overview"] });
      const owner = data.ownerUserId ? orgPeople.find((m) => m.user.id === data.ownerUserId) : undefined;
      form.reset();
      mutation.reset();
      onCreated(created, owner ? (owner.user.name || owner.user.email || "them") : null);
    },
  });

  function handleClose() {
    form.reset();
    mutation.reset();
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create a new Boo</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="posture"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {POSTURE_OPTIONS.map((opt) => {
                        const active = field.value === opt.value;
                        return (
                          <label
                            key={opt.value}
                            className={`flex cursor-pointer flex-col gap-1 rounded-xl border px-4 py-3 transition-colors ${
                              active ? "border-foreground bg-muted" : "border-border hover:bg-muted/40"
                            }`}
                          >
                            <input
                              type="radio"
                              className="sr-only"
                              name={field.name}
                              value={opt.value}
                              checked={active}
                              onChange={() => field.onChange(opt.value)}
                            />
                            <span className="text-sm font-medium">{POSTURE_LABELS[opt.value]}</span>
                            <span className="text-xs text-muted-foreground">{opt.blurb}</span>
                          </label>
                        );
                      })}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="my-boo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Optional" rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {orgPeople.length > 0 && (
              <FormField
                control={form.control}
                name="ownerUserId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Owner</FormLabel>
                    <Select value={field.value || "me"} onValueChange={(v) => field.onChange(v === "me" ? "" : v)}>
                      <FormControl>
                        <SelectTrigger id="create-boo-owner" className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="me">You</SelectItem>
                        {orgPeople.map((m) => (
                          <SelectItem key={m.user.id} value={m.user.id}>
                            {m.user.name || m.user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      The owner decides who else can open and manage this Boo.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            {mutation.isError && <InlineError error={mutation.error} />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" loading={mutation.isPending}>
                Create
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Overview ─────────────────────────────────────────────────────────────────
// The org's Boos under its name, as a list with an Open button per Boo or as
// the org chart (each Boo above the Boos it uses skills and memories from).
// Phones have no room for the chart and only get the list.
function BooOverview({
  orgName,
  boos,
  links,
  view,
  canChooseView,
  onChooseView,
  canCreate,
  onCreate,
  onOpen,
  onLink,
  onOpenSharing,
}: {
  orgName: string;
  boos: ContainerRow[];
  links: BooMapLink[];
  view: BoosView;
  canChooseView: boolean;
  onChooseView: (view: BoosView) => void;
  canCreate: boolean;
  onCreate: () => void;
  onOpen: (c: ContainerRow) => void;
  onLink: (c: ContainerRow) => void;
  onOpenSharing: (c: ContainerRow) => void;
}) {
  const hasLinks = links.length > 0;
  const showTabs = canChooseView && boos.length > 0;
  return (
    <Tabs
      value={view}
      onValueChange={(v) => {
        if (isBoosView(v)) onChooseView(v);
      }}
      className="gap-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 className="truncate text-base font-semibold">
            {orgName}{" "}
            <span className="font-normal text-muted-foreground tabular-nums">
              ({boos.length} {boos.length === 1 ? "Boo" : "Boos"})
            </span>
          </h2>
          <p className="text-xs text-muted-foreground text-pretty">
            {hasLinks
              ? view === "list"
                ? "Each Boo is listed above the Boos it learns from."
                : "Each Boo sits above the Boos it learns from. Dashed lines are requests waiting for approval."
              : boos.length < 2
                ? "Once you have a second Boo, one can learn from the other."
                : "These Boos don't learn from each other yet. To link two, open the ⋮ menu on a Boo and choose “Learn from another Boo.”"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {showTabs && (
            <TabsList aria-label="Show Boos as">
              <TabsTrigger value="list" className="px-2.5">
                <List />
                List
              </TabsTrigger>
              <TabsTrigger value="diagram" className="px-2.5">
                <Network />
                Diagram
              </TabsTrigger>
            </TabsList>
          )}
          {canCreate && (
            <Button size="sm" onClick={onCreate} className="active:scale-[0.96] transition-transform">
              <Plus />
              New Boo
            </Button>
          )}
        </div>
      </div>
      {boos.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-xl bg-muted/30 px-6 py-12 text-center shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)] dark:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]">
          <p className="font-medium">No Boos yet</p>
          <p className="text-sm text-muted-foreground">
            {canCreate
              ? "Create your first Boo to get started."
              : "When someone in your organization creates a Boo, you'll see it here."}
          </p>
        </div>
      ) : (
        <>
          <TabsContent value="list">
            <BooList
              boos={boos}
              links={links}
              onOpen={onOpen}
              onLink={onLink}
              onOpenSharing={onOpenSharing}
            />
          </TabsContent>
          <TabsContent value="diagram">
            <BooOrgChart
              boos={boos}
              links={links}
              onOpen={onOpen}
              onLink={onLink}
              onOpenSharing={onOpenSharing}
            />
          </TabsContent>
        </>
      )}
    </Tabs>
  );
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function ContainersDashboardPage() {
  useDocumentTitle("Dashboard");
  const navigate = useNavigate();
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const [createOpen, setCreateOpen] = useState(false);
  // The Boo whose card menu asked to use another Boo.
  const [linkingFrom, setLinkingFrom] = useState<ContainerRow | null>(null);
  const { data: session } = useSession();

  const { data: listResponse, isLoading, isError, error } = useQuery({
    queryKey: ["containers"],
    queryFn: api.containers.list,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.data.some((r) => r.deploymentStatus === "creating" || r.deploymentStatus === "restarting") ? 5000 : false;
    },
  });
  const data = listResponse?.data;
  const permissions = listResponse?._permissions;
  // Links come from the backend's record, so the map loads even when Boos
  // are stopped.
  const mapQuery = useQuery({ queryKey: ["boo-map"], queryFn: api.booMap.get });
  const { org } = useOrgRole();
  const narrow = useMediaQuery("(max-width: 767px)");
  // The user's pick of list or diagram for this org, if they made one.
  const [viewChoice, setViewChoice] = useState(() => ({ orgId, view: storedBoosView(orgId) }));
  const chosenView = viewChoice.orgId === orgId ? viewChoice.view : storedBoosView(orgId);
  const chooseView = (view: BoosView) => {
    storeBoosView(orgId, view);
    setViewChoice({ orgId, view });
  };

  function openContainer(c: ContainerRow) {
    // Default tab on open is Chat (per the dashboard restructure plan).
    navigate({ to: "/orgs/$orgId/containers/$containerId/chat", params: { orgId, containerId: c.id } });
  }

  const openSharing = useCallback(
    (c: ContainerRow) =>
      navigate({ to: "/orgs/$orgId/containers/$containerId/sharing", params: { orgId, containerId: c.id } }),
    [navigate, orgId],
  );

  function handleCreated(c: ContainerRow, ownerName: string | null) {
    setCreateOpen(false);
    // A Boo made for someone else is theirs to open; stay on the chart.
    if (ownerName) {
      toast.success(`${c.name} is being set up for ${ownerName}.`);
      return;
    }
    // Send the user straight into their new agent so they see
    // provisioning progress (status badge in header, build feed in Logs tab).
    navigate({ to: "/orgs/$orgId/containers/$containerId/chat", params: { orgId, containerId: c.id } });
  }

  // Personal greeting — first word of the display name, or just "Hello" if
  // we don't have one yet (e.g. signed in without setting a name).
  const fullName = session?.user.name?.trim();
  const firstName = fullName ? fullName.split(/\s+/)[0] : null;

  return (
    <div className="p-6 space-y-6">
      <Collapsible>
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">
            <BooGradientText>
              {firstName ? "Hello," : "Hello"}
            </BooGradientText>
            {firstName ? ` ${firstName}` : ""}
          </h1>
          {/* "Learn more" sits inline with the subtitle so it reads as part
              of the same line of copy rather than a top-right CTA. */}
          <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-muted-foreground">
            <span>Your Boos are secure, fully capable, and ready to ship work.</span>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="group inline-flex items-center gap-0.5 underline decoration-dotted underline-offset-4 hover:text-foreground transition-colors"
              >
                See what&apos;s inside
                <ChevronDown className="size-3.5 transition-transform group-data-[state=open]:rotate-180" />
              </button>
            </CollapsibleTrigger>
          </p>
        </div>

        <CollapsibleContent className="mt-4">
          <AgentHighlights />
        </CollapsibleContent>
      </Collapsible>

      <section>
        {isLoading ? (
          <LoadingIndicator />
        ) : isError || !data ? (
          <ErrorComponent error={error ?? "Failed to load Boos"} />
        ) : (
          <BooOverview
            orgName={org?.name ?? "Your organization"}
            boos={data}
            links={mapQuery.data?.links ?? []}
            view={narrow ? "list" : (chosenView ?? defaultBoosView(data.length))}
            canChooseView={!narrow}
            onChooseView={chooseView}
            canCreate={permissions?.canCreate !== false}
            onCreate={() => setCreateOpen(true)}
            onOpen={openContainer}
            onLink={setLinkingFrom}
            onOpenSharing={openSharing}
          />
        )}
      </section>

      {linkingFrom && (
        <RequestAccessSheet
          consumer={{ id: linkingFrom.id, name: linkingFrom.name }}
          candidates={data ?? []}
          onClose={() => setLinkingFrom(null)}
        />
      )}

      <CreateContainerDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
